/* ==========================================================================
   DryWell theme scripts
   Three cinematic moments only:
     1. Welcome intro (homepage, first visit per session)
     2. Hero opening (fogged window you can wipe, settles into a window frame)
     3. Closing wordmark (zoom through the DryWell letters)
   Everything else is plain, fast, functional UI.
   GSAP + ScrollTrigger + SplitText are loaded before this file.
   ========================================================================== */
(() => {
  'use strict';

  const D = window.Drywell || {};
  const html = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasGsap = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';
  const motion = hasGsap && !reduced;
  const designMode = !!(window.Shopify && window.Shopify.designMode);

  if (reduced) html.classList.add('reduced-motion');
  if (hasGsap) {
    gsap.registerPlugin(ScrollTrigger);
    if (window.SplitText) gsap.registerPlugin(SplitText);
  }

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  /* ------------------------------------------------------------------
     Money
  ------------------------------------------------------------------ */
  function formatMoney(cents, format = D.moneyFormat || '£{{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    const fmt = (n, p = 2, t = ',', d = '.') => {
      const num = (n / 100).toFixed(p);
      const [whole, dec] = num.split('.');
      return whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${t}`) + (dec ? d + dec : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        default: return fmt(cents, 2);
      }
    });
  }

  /* ------------------------------------------------------------------
     1. WELCOME INTRO
     The <head> script decides before first paint whether to play it
     (homepage, first visit this session) by adding html.dw-intro.
     CSS hides it on its own after 6s even if this script never runs.
  ------------------------------------------------------------------ */
  function finishIntro(el) {
    html.classList.remove('dw-intro');
    if (el && el.parentNode) el.remove();
    const logo = $('.header__logo');
    if (logo) logo.style.opacity = '';
  }

  function runIntro() {
    const el = $('[data-intro]');
    const active = html.classList.contains('dw-intro');
    if (!el || !active) { finishIntro(el); return Promise.resolve({ played: false }); }
    if (!motion || designMode) { finishIntro(el); return Promise.resolve({ played: false }); }

    const bg = $('.intro__bg', el);
    const drop = $('.intro__drop', el);
    const ripples = $$('.intro__ripple', el);
    const word = $('.intro__word', el);
    const line = $('.intro__line', el);
    const logo = $('.header__logo');

    let chars = [word];
    if (window.SplitText) chars = SplitText.create(word, { type: 'chars', mask: 'chars' }).chars;

    return new Promise((resolve) => {
      let opened = false;
      const open = () => { if (!opened) { opened = true; resolve({ played: true }); } };

      gsap.set(word, { opacity: 1 });
      gsap.set(chars, { yPercent: 110 });

      // If the page took a while to load, skip straight to the reveal.
      const late = performance.now() > 1800;
      const tl = gsap.timeline();
      if (late) {
        gsap.set(chars, { yPercent: 0 });
        gsap.set(line, { scaleX: 1 });
        tl.addLabel('exit', 0.35);
      } else {
        tl.fromTo(drop, { y: () => -innerHeight * 0.45, opacity: 1 }, { y: 0, duration: 0.5, ease: 'power2.in' })
          .to(drop, { scaleY: 0.3, scaleX: 1.6, opacity: 0, duration: 0.12, ease: 'power1.out' })
          .fromTo(ripples, { scale: 0.2, opacity: 1 }, { scale: 3.4, opacity: 0, duration: 0.9, stagger: 0.12, ease: 'power2.out' }, '<')
          .to(chars, { yPercent: 0, duration: 0.8, stagger: 0.035, ease: 'expo.out' }, '<0.02')
          .fromTo(line, { scaleX: 0 }, { scaleX: 1, duration: 0.45, ease: 'power3.inOut' }, '-=0.45')
          .addLabel('exit', '+=0.15');
      }
      const skipBtn = $('[data-intro-skip]', el);
      tl.to(line, { opacity: 0, duration: 0.25 }, 'exit')
        .to(skipBtn, { opacity: 0, duration: 0.2 }, 'exit')
        .set(el, { pointerEvents: 'none' }, 'exit+=0.3')
        .add(open, 'exit')
        .fromTo(bg, { clipPath: 'inset(0% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 100% 0%)', duration: 0.9, ease: 'expo.inOut' }, 'exit');

      // The big wordmark glides into the header logo spot, then hands over.
      if (logo && window.scrollY < 40) {
        tl.to(word, {
          duration: 0.9, ease: 'expo.inOut',
          x: () => { const a = logo.getBoundingClientRect(), b = word.getBoundingClientRect(); return (a.left + a.width / 2) - (b.left + b.width / 2); },
          y: () => { const a = logo.getBoundingClientRect(), b = word.getBoundingClientRect(); return (a.top + a.height / 2) - (b.top + b.height / 2); },
          scale: () => { const a = logo.getBoundingClientRect(), b = word.getBoundingClientRect(); return b.width ? a.width / b.width : 0.2; }
        }, 'exit')
          .to(logo, { opacity: 1, duration: 0.15 }, 'exit+=0.78')
          .to(word, { opacity: 0, duration: 0.15 }, 'exit+=0.78');
      } else {
        tl.to(word, { opacity: 0, y: -40, duration: 0.6, ease: 'power2.in' }, 'exit');
      }

      // Visitors can always skip: click, key, wheel or swipe fast-forwards it.
      const skip = () => { tl.timeScale(6); cleanup(); };
      const cleanup = () => {
        ['click', 'keydown', 'wheel', 'touchmove'].forEach((ev) => window.removeEventListener(ev, skip, true));
      };
      ['click', 'keydown', 'wheel', 'touchmove'].forEach((ev) => window.addEventListener(ev, skip, { capture: true, passive: true }));
      tl.eventCallback('onComplete', () => { cleanup(); open(); finishIntro(el); });
    });
  }

  /* ------------------------------------------------------------------
     Header: solid once past the hero, hides on scroll down
  ------------------------------------------------------------------ */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const shopMenu = $('[data-shop-menu]', header);
    if (shopMenu) {
      const summary = $('summary', shopMenu);
      const closeShopMenu = (restoreFocus = false) => {
        shopMenu.open = false;
        if (restoreFocus) summary.focus();
      };
      shopMenu.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && shopMenu.open) {
          e.preventDefault();
          closeShopMenu(true);
        }
      });
      shopMenu.addEventListener('focusout', (e) => {
        if (!shopMenu.contains(e.relatedTarget)) closeShopMenu();
      });
      document.addEventListener('click', (e) => {
        if (shopMenu.open && !shopMenu.contains(e.target)) closeShopMenu();
      });
      const desktopNavigation = window.matchMedia('(min-width: 1600px)');
      if (desktopNavigation.addEventListener) {
        desktopNavigation.addEventListener('change', (e) => {
          if (!e.matches) closeShopMenu();
        });
      }
    }
    const hero = $('[data-hero]');
    let last = window.scrollY;
    const update = () => {
      const y = window.scrollY;
      const heroEnd = hero ? hero.offsetTop + hero.offsetHeight - 80 : 10;
      header.classList.toggle('is-solid', y > heroEnd || !hero);
      const down = y > last + 2, up = y < last - 2;
      if (y < 200 || up) header.classList.remove('is-hidden');
      else if (down && !shopMenu?.open && !header.contains(document.activeElement)) header.classList.add('is-hidden');
      last = y;
    };
    addEventListener('scroll', update, { passive: true });
    update();

    const overlay = $('[data-menu]');
    const open = $('[data-menu-open]');
    const close = $('[data-menu-close]');
    if (overlay && open) {
      let menuBackground = [];
      let previousOverflow = '';
      const set = (state) => {
        if (state && !overlay.classList.contains('is-open')) {
          previousOverflow = document.body.style.overflow;
          menuBackground = $$('.main, [data-header], .announce, .footer, [data-help], [data-satc]').map((el) => ({ el, inert: el.inert }));
          menuBackground.forEach(({ el }) => { el.inert = true; });
        } else if (!state) {
          menuBackground.forEach(({ el, inert }) => { el.inert = inert; });
          menuBackground = [];
        }
        overlay.classList.toggle('is-open', state);
        overlay.setAttribute('aria-hidden', String(!state));
        open.setAttribute('aria-expanded', String(state));
        document.body.style.overflow = state ? 'hidden' : previousOverflow;
        if (state) close && close.focus(); else open.focus();
      };
      open.addEventListener('click', () => set(true));
      close && close.addEventListener('click', () => set(false));
      overlay.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { set(false); return; }
        if (e.key !== 'Tab') return;
        const controls = $$('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]', overlay).filter((el) => el.getClientRects().length);
        const first = controls[0], last = controls[controls.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last && last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first && first.focus(); }
      });
      $$('a', overlay).forEach((a) => a.addEventListener('click', () => set(false)));
    }
  }

  /* ------------------------------------------------------------------
     Videos play only while on screen (saves data and battery)
  ------------------------------------------------------------------ */
  function loadVideo(v) {
    if (v.dataset.dwLoaded) return;
    $$('source[data-src]', v).forEach((source) => { source.src = source.dataset.src; source.removeAttribute('data-src'); });
    v.dataset.dwLoaded = '1';
    v.load();
  }
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (isIntersecting && !document.hidden) { loadVideo(target); const p = target.play(); p && p.catch(() => {}); }
      else target.pause();
    });
  }, { rootMargin: '150px 0px' }) : null;

  function initAutoplay(root) {
    $$('video[data-autoplay]', root).forEach((v) => {
      if (v.dataset.dwAuto) return;
      v.dataset.dwAuto = '1';
      v.muted = true; v.loop = true;
      if (reduced) { v.removeAttribute('autoplay'); return; }
      if (io) io.observe(v); else { loadVideo(v); v.play().catch(() => {}); }
    });
  }

  /* ------------------------------------------------------------------
     2. HERO OPENING: fogged window you can wipe, then it settles into
     a window frame as you scroll
  ------------------------------------------------------------------ */
  function FogGlass(canvas, video) {
    const ctx = canvas.getContext('2d');
    const mask = document.createElement('canvas');
    const mctx = mask.getContext('2d');
    const tiny = document.createElement('canvas');
    const tctx = tiny.getContext('2d');
    const beads = document.createElement('canvas');
    const brush = document.createElement('canvas');
    const SCALE = 0.5;
    const LIFE = 7000, HOLD = 2600;
    let w = 0, h = 0, points = [], running = false, visible = true, last = null, raf = 0;
    const ink = getComputedStyle(html).getPropertyValue('--ink-rgb').trim() || '29, 43, 51';
    const fog = getComputedStyle(html).getPropertyValue('--fog-rgb').trim() || '232, 238, 240';

    function makeBrush(r) {
      brush.width = brush.height = r * 2;
      const b = brush.getContext('2d');
      const g = b.createRadialGradient(r, r, 0, r, r, r);
      g.addColorStop(0, 'rgba(0,0,0,1)'); g.addColorStop(0.55, 'rgba(0,0,0,.9)'); g.addColorStop(1, 'rgba(0,0,0,0)');
      b.fillStyle = g; b.fillRect(0, 0, r * 2, r * 2);
    }
    function makeBeads() {
      beads.width = w; beads.height = h;
      const b = beads.getContext('2d');
      const count = Math.round((w * h) / 260);
      for (let i = 0; i < count; i++) {
        const x = Math.random() * w, y = Math.random() * h;
        const r = Math.random() < 0.93 ? Math.random() * 1.4 + 0.3 : Math.random() * 3.5 + 1.5;
        b.fillStyle = `rgba(${fog}, ${0.05 + Math.random() * 0.12})`;
        b.beginPath(); b.arc(x, y, r, 0, Math.PI * 2); b.fill();
        if (r > 1.5) {
          b.fillStyle = `rgba(255,255,255,${0.18 + Math.random() * 0.2})`;
          b.beginPath(); b.arc(x - r * 0.35, y - r * 0.35, r * 0.35, 0, Math.PI * 2); b.fill();
        }
      }
    }
    function resize() {
      const r = canvas.getBoundingClientRect();
      w = canvas.width = mask.width = Math.max(2, Math.round(r.width * SCALE));
      h = canvas.height = mask.height = Math.max(2, Math.round(r.height * SCALE));
      tiny.width = Math.max(16, Math.round(w / 22));
      tiny.height = Math.max(16, Math.round(h / 22));
      makeBrush(Math.round(Math.max(w, h) * 0.055));
      makeBeads();
    }
    function coverDraw(c, img, cw, ch) {
      const vw = img.videoWidth, vh = img.videoHeight;
      if (!vw) return false;
      const s = Math.max(cw / vw, ch / vh);
      const sw = cw / s, sh = ch / s;
      c.drawImage(img, (vw - sw) / 2, (vh - sh) / 2, sw, sh, 0, 0, cw, ch);
      return true;
    }
    function addPoint(x, y) {
      const now = performance.now();
      if (last) {
        const dx = x - last.x, dy = y - last.y;
        const dist = Math.hypot(dx, dy);
        const step = brush.width * 0.22;
        for (let d = step; d < dist; d += step) points.push({ x: last.x + dx * d / dist, y: last.y + dy * d / dist, t: now });
      }
      points.push({ x, y, t: now });
      last = { x, y };
      if (points.length > 1400) points.splice(0, points.length - 1400);
    }
    function frame() {
      raf = requestAnimationFrame(frame);
      if (!visible) return;
      const now = performance.now();
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      if (video.readyState >= 2 && coverDraw(tctx, video, tiny.width, tiny.height)) {
        ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(tiny, 0, 0, w, h);
      } else {
        ctx.fillStyle = `rgb(${ink})`; ctx.fillRect(0, 0, w, h);
      }
      ctx.fillStyle = `rgba(${ink}, .46)`; ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = `rgba(${fog}, .09)`; ctx.fillRect(0, 0, w, h);
      ctx.drawImage(beads, 0, 0);
      mctx.clearRect(0, 0, w, h);
      points = points.filter((p) => now - p.t < LIFE);
      const half = brush.width / 2;
      for (const p of points) {
        const age = now - p.t;
        mctx.globalAlpha = age < HOLD ? 1 : 1 - (age - HOLD) / (LIFE - HOLD);
        mctx.drawImage(brush, p.x - half, p.y - half);
      }
      mctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.drawImage(mask, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
    }
    const toLocal = (e) => {
      const r = canvas.getBoundingClientRect();
      return [(e.clientX - r.left) * SCALE, (e.clientY - r.top) * SCALE];
    };
    canvas.addEventListener('pointermove', (e) => addPoint(...toLocal(e)), { passive: true });
    canvas.addEventListener('pointerdown', (e) => { last = null; addPoint(...toLocal(e)); }, { passive: true });
    canvas.addEventListener('pointerleave', () => { last = null; });

    return {
      start() { if (running) return; running = true; resize(); frame(); addEventListener('resize', resize); },
      setVisible(v) { visible = v; },
      demo() {
        const path = { p: 0 };
        last = null;
        return gsap.to(path, {
          p: 1, duration: 1.6, ease: 'power2.inOut',
          onUpdate: () => {
            const t = path.p;
            addPoint(w * (0.52 + 0.36 * t), h * (0.32 + Math.sin(t * Math.PI * 2.2) * 0.08 + t * 0.16));
          },
          onComplete: () => { last = null; }
        });
      }
    };
  }

  function initHero(section) {
    const video = $('.hero__video', section);
    const canvas = $('[data-fog]', section);
    const frame = $('.hero__frame', section);
    const title = $('.hero__title', section);
    const copy = $('.hero__copy', section);
    const hint = $('.hero__hint', section);
    if (video) {
      video.muted = true;
      if (reduced) { video.removeAttribute('autoplay'); video.pause(); }
      else { const p = video.play(); p && p.catch(() => {}); }
    }
    if (!motion) return null;

    let fog = null;
    if (canvas && video && canvas.getContext) {
      fog = FogGlass(canvas, video);
      fog.start();
      ScrollTrigger.create({
        trigger: section, start: 'top top', end: 'bottom top',
        onToggle: (self) => fog.setVisible(self.isActive)
      });
    }

    // Headline assembles as the welcome intro lifts (or straight away).
    let split = null;
    if (title && window.SplitText) split = SplitText.create(title, { type: 'lines,words,chars', mask: 'lines', linesClass: 'split-line' });
    const sub = $$('.hero__sub, .hero__copy .btn, .hero__trust', section);
    gsap.set(sub, { opacity: 0, y: 24 });
    if (split) gsap.set(split.chars, { yPercent: 120 });

    const intro = () => {
      const tl = gsap.timeline();
      tl.fromTo(video, { scale: 1.3 }, { scale: 1.1, duration: 2.2, ease: 'expo.out' }, 0);
      if (split) tl.to(split.chars, { yPercent: 0, duration: 1.1, stagger: 0.02, ease: 'expo.out' }, 0.1);
      tl.to(sub, { opacity: 1, y: 0, duration: 0.9, stagger: 0.08, ease: 'expo.out' }, 0.5);
      if (hint) tl.from(hint, { opacity: 0, duration: 0.8 }, 1);
      if (fog) tl.add(() => fog.demo(), 0.9);
      return tl;
    };

    // Scroll: the fog lifts and the view settles into a window frame.
    const mm = gsap.matchMedia();
    mm.add({ mobile: '(max-width: 749px)', desktop: '(min-width: 750px)' }, (c) => {
      const inset = c.conditions.mobile ? '18% 6% 18% 6%' : '15% 24% 15% 24%';
      const radius = getComputedStyle(html).getPropertyValue('--radius-pane').trim() || '22px';
      gsap.set($$('.hero__after p', section), { y: 24 });
      const tl = gsap.timeline({
        scrollTrigger: { trigger: section, start: 'top top', end: c.conditions.mobile ? '+=80%' : '+=100%', scrub: 0.8, pin: $('.hero__pin', section), anticipatePin: 1 }
      });
      tl.to(canvas, { opacity: 0, ease: 'none', duration: 0.35 }, 0)
        .set(canvas, { pointerEvents: 'none' }, 0.3)
        .to(copy, { yPercent: -25, opacity: 0, ease: 'none', duration: 0.3 }, 0)
        .to(hint, { opacity: 0, duration: 0.1 }, 0)
        .fromTo(frame, { clipPath: 'inset(0% 0% 0% 0% round 0px)' }, { clipPath: `inset(${inset} round ${radius})`, ease: 'power2.inOut', duration: 0.7 }, 0.25)
        .to(video, { scale: 1, ease: 'none', duration: 1 }, 0)
        .to($('.hero__mullion .v', section), { scaleY: 1, ease: 'power3.inOut', duration: 0.3 }, 0.6)
        .to($('.hero__mullion .h', section), { scaleX: 1, ease: 'power3.inOut', duration: 0.3 }, 0.66)
        .to($$('.hero__after p', section), { opacity: 1, y: 0, stagger: 0.08, duration: 0.25 }, 0.72);
    });

    return intro;
  }

  /* ------------------------------------------------------------------
     3. CLOSING WORDMARK: the video plays inside the letters, then we
     zoom through them into the final call to action
  ------------------------------------------------------------------ */
  function initFinale(section) {
    initAutoplay(section);
    const maskEl = $('.finale__mask', section);
    const cta = $('.finale__cta', section);
    const shade = $('.finale__shade', section);
    if (!motion) {
      if (maskEl) maskEl.style.display = 'none';
      if (cta) { cta.style.opacity = 1; cta.classList.add('is-live'); }
      if (shade) shade.style.opacity = 1;
      return;
    }
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section, start: 'top top', end: '+=100%', scrub: 0.8, pin: $('.finale__pin', section), anticipatePin: 1,
        onUpdate: (self) => cta && cta.classList.toggle('is-live', self.progress > 0.7)
      }
    });
    tl.fromTo(maskEl, { scale: 1 }, { scale: 14, ease: 'power3.in', duration: 1 }, 0)
      .to(maskEl, { opacity: 0, ease: 'none', duration: 0.25 }, 0.55)
      .to(shade, { opacity: 1, duration: 0.3 }, 0.6)
      .fromTo(cta, { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.3 }, 0.62);
  }

  /* ------------------------------------------------------------------
     Static fallbacks for sections that used to be scroll-driven
  ------------------------------------------------------------------ */
  function initScrubStatic(section) {
    section.classList.add('scrub--static');
    const video = $('video', section);
    if (video) { video.setAttribute('data-autoplay', ''); initAutoplay(section); }
    const to = Number(section.dataset.to) || 48;
    const num = $('[data-dial-num]', section);
    const fill = $('.dial__fill', section);
    const C = 2 * Math.PI * 90;
    if (num) num.textContent = to;
    if (fill) fill.setAttribute('stroke-dasharray', `${(C * 0.75 * to / 100).toFixed(1)} ${C.toFixed(1)}`);
    const dial = $('.dial', section);
    if (dial) dial.classList.add('is-dry');
  }

  /* ------------------------------------------------------------------
     FAQ accordion
  ------------------------------------------------------------------ */
  function initFaq(root) {
    $$('.faq__q', root).forEach((btn) => {
      if (btn.dataset.dwFaq) return;
      btn.dataset.dwFaq = '1';
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        if (hasGsap) setTimeout(() => ScrollTrigger.refresh(), 600);
      });
    });
  }

  /* ------------------------------------------------------------------
     Product form: variants, quantity, gallery
  ------------------------------------------------------------------ */
  function formFeedback(form, message, error = false) {
    let el = $('[data-form-feedback]', form);
    if (!el) { el = document.createElement('p'); el.className = 'form-feedback'; el.dataset.formFeedback = ''; el.setAttribute('role', 'status'); el.setAttribute('aria-live', 'polite'); form.append(el); }
    el.hidden = !message; el.textContent = message || ''; el.classList.toggle('is-error', error);
    return el;
  }

  async function addToCart(form) {
    const res = await fetch(D.routes.cartAdd + '.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
    const result = await res.json();
    if (!res.ok) throw new Error(result.description || D.strings.error);
    formFeedback(form, D.strings.addedToCart);
    try { await Cart.refresh(true); }
    catch (_) {
      const status = formFeedback(form, D.strings.cartOpenFailed);
      const link = document.createElement('a'); link.href = D.routes.cart; link.textContent = 'View cart'; link.className = 'link';
      status.append(' ', link);
    }
  }

  function initProduct(root) {
    $$('[data-product-form]', root).forEach((form) => {
      if (form.dataset.dwForm) return;
      form.dataset.dwForm = '1';
      const scope = form.closest('[data-product]') || root;
      const json = $('[data-product-json]', scope);
      let product = null;
      try { product = json ? JSON.parse(json.textContent) : null; } catch (_) {};
      const idInput = $('[name="id"]', form);
      const btn = $('[data-add]', form);
      if (!product || !Array.isArray(product.variants) || !btn || !idInput) return;
      const priceEl = $('[data-price]', scope);
      const bars = $$('[data-satc]').filter((bar) => bar.dataset.for === scope.id);
      const fields = $$('.variant-picker', form);
      let current = product.variants.find((v) => String(v.id) === idInput.value);
      let busy = false;
      const syncButtons = () => {
        const text = busy ? D.strings.adding : !current ? D.strings.unavailable : current.available ? D.strings.addToCart : D.strings.soldOut;
        const disabled = busy || !current || !current.available;
        [btn, ...bars.map((b) => $('[data-satc-btn]', b))].filter(Boolean).forEach((b) => { b.disabled = disabled; b.setAttribute('aria-busy', String(busy)); $('.btn__label', b).textContent = text; });
        form.setAttribute('aria-busy', String(busy));
      };
      const qtyInput = $('input[name="quantity"]', form);
      if (qtyInput) {
        const normaliseQty = () => { qtyInput.value = Math.max(1, Math.floor(Number(qtyInput.value) || 1)); };
        qtyInput.addEventListener('change', normaliseQty);
        $$('[data-qty]', form).forEach((b) => b.addEventListener('click', () => { qtyInput.value = Math.max(1, Math.floor(Number(qtyInput.value) || 1) + Number(b.dataset.qty)); }));
      }
      const update = () => {
        const chosen = fields.map((fs) => { const c = $('input:checked', fs); return c && c.value; });
        current = fields.length ? product.variants.find((v) => v.options.every((o, i) => o === chosen[i])) : product.variants.find((v) => String(v.id) === idInput.value);
        idInput.value = current ? current.id : '';
        syncButtons();
        if (!current) { formFeedback(form, D.strings.variantUnavailable, true); return; }
        formFeedback(form, '');
        if (priceEl) {
          priceEl.replaceChildren();
          const now = document.createElement('span'); now.className = 'price__now'; now.textContent = formatMoney(current.price); priceEl.append(now);
          if (current.compare_at_price > current.price) {
            const was = document.createElement('s'); was.className = 'price__was'; was.textContent = formatMoney(current.compare_at_price);
            const save = document.createElement('span'); save.className = 'price__save'; save.textContent = (D.strings.save || 'Save __amount__').replace('__amount__', formatMoney(current.compare_at_price - current.price));
            priceEl.append(was, save);
          }
          priceEl.classList.toggle('price--sale', current.compare_at_price > current.price);
        }
        bars.forEach((bar) => {
          $('[data-satc-price]', bar).textContent = formatMoney(current.price);
          const variantText = $('[data-satc-variant]', bar); if (variantText) variantText.textContent = current.title;
          const thumb = $('.satc__thumb img', bar);
          if (thumb && current.studio_image) { thumb.removeAttribute('srcset'); thumb.src = current.studio_image; }
        });
        const delivery = $('[data-delivery]', scope);
        if (delivery && Number(delivery.dataset.threshold) > 0) {
          const label = $('[data-delivery-label]', delivery);
          if (label) label.textContent = current.price >= Number(delivery.dataset.threshold) ? delivery.dataset.freeLabel : delivery.dataset.thresholdLabel;
        }
        if (current.featured_media) showMedia(scope, current.featured_media.id);
        if (document.body.classList.contains('template-product')) {
          const url = new URL(location.href); url.searchParams.set('variant', current.id); history.replaceState({}, '', url);
        }
      };
      fields.forEach((fs) => $$('input', fs).forEach((i) => i.addEventListener('change', update)));
      const select = $('[data-variant-select]', form);
      if (select) select.addEventListener('change', () => {
        const v = product.variants.find((x) => String(x.id) === select.value);
        if (!v) return;
        fields.forEach((fs, i) => $$('input', fs).forEach((radio) => { radio.checked = radio.value === v.options[i]; }));
        update();
      });
      form.classList.add('is-enhanced');
      syncButtons();
      form.addEventListener('submit', async (e) => {
        if (busy || !current || !current.available) { e.preventDefault(); return; }
        if (qtyInput) qtyInput.value = Math.max(1, Math.floor(Number(qtyInput.value) || 1));
        if (D.settings && D.settings.cartType === 'page') return;
        e.preventDefault(); busy = true; syncButtons(); formFeedback(form, '');
        try { await addToCart(form); }
        catch (err) { formFeedback(form, err.message || D.strings.error, true); }
        finally { busy = false; syncButtons(); }
      });
    });
    $$('[data-thumb]', root).forEach((t) => {
      if (t.dataset.dwThumb) return; t.dataset.dwThumb = '1';
      t.addEventListener('click', () => showMedia(t.closest('[data-product]') || root, t.dataset.thumb));
    });
  }
  function showMedia(scope, id) {
    const slides = $$('[data-media]', scope);
    if (!slides.length || !slides.some((slide) => String(slide.dataset.media) === String(id))) return;
    slides.forEach((slide) => {
      slide.hidden = String(slide.dataset.media) !== String(id);
      if (slide.hidden) $$('video', slide).forEach((video) => video.pause());
    });
    $$('[data-thumb]', scope).forEach((thumb) => thumb.setAttribute('aria-current', String(String(thumb.dataset.thumb) === String(id))));
  }

  /* ------------------------------------------------------------------
     Sticky bar: follows the selected variant and prevents duplicate adds
  ------------------------------------------------------------------ */
  function initStickyBar() {
    $$('[data-satc]').forEach((bar) => {
      if (bar.dataset.dwSticky) return;
      const scope = document.getElementById(bar.dataset.for);
      const form = scope && $('[data-product-form]', scope);
      const buyBtn = form && $('[data-add]', form);
      if (!buyBtn || !('IntersectionObserver' in window)) return;
      bar.dataset.dwSticky = '1';
      const syncPosition = () => {
        const past = buyBtn.getBoundingClientRect().bottom < 0;
        bar.classList.toggle('is-visible', past);
        bar.inert = !past || document.body.classList.contains('has-cart');
        document.body.classList.toggle('has-satc', $$('[data-satc].is-visible').length > 0);
      };
      const obs = new IntersectionObserver(syncPosition);
      obs.observe(buyBtn);
      let scheduled = false;
      const onScroll = () => {
        if (!bar.isConnected) { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); obs.disconnect(); return; }
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => { scheduled = false; syncPosition(); });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      syncPosition();
      $('[data-satc-btn]', bar).addEventListener('click', () => {
        if (buyBtn.disabled) return;
        if (form.requestSubmit) form.requestSubmit(buyBtn); else buyBtn.click();
      });
    });
  }

  /* ------------------------------------------------------------------
     Quick add: persistent feedback and no duplicate network submissions
  ------------------------------------------------------------------ */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-quick-add]');
    if (!form || (D.settings && D.settings.cartType === 'page')) return;
    e.preventDefault();
    const btn = $('button[type="submit"]', form);
    if (!btn || btn.disabled || form.dataset.busy === 'true') return;
    const label = $('.btn__label', btn) || btn;
    const original = label.textContent;
    form.dataset.busy = 'true'; btn.disabled = true; btn.setAttribute('aria-busy', 'true'); label.textContent = D.strings.adding;
    formFeedback(form, '');
    try { await addToCart(form); }
    catch (err) { formFeedback(form, err.message || D.strings.error, true); }
    finally { form.dataset.busy = 'false'; btn.disabled = false; btn.setAttribute('aria-busy', 'false'); label.textContent = original; }
  });

  /* ------------------------------------------------------------------
     Cart drawer
  ------------------------------------------------------------------ */
  const Cart = {
    el: null,
    busy: false,
    itemCount: 0,
    opener: null,
    background: [],
    init() {
      this.el = $('[data-cart-drawer]');
      if (!this.el) return;
      $$('[data-cart-close]', this.el).forEach((b) => b.addEventListener('click', () => this.close()));
      this.el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key !== 'Tab') return;
        const focusable = $$('a[href], button:not(:disabled), input:not(:disabled), [tabindex="0"]', this.el).filter((el) => el.getClientRects().length);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === $('.drawer__panel', this.el))) { e.preventDefault(); last && last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first && first.focus(); }
      });
      document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-cart-open]');
        if (t && !(D.settings && D.settings.cartType === 'page')) { e.preventDefault(); this.refresh(true).catch(() => { window.location.href = D.routes.cart; }); }
      });
      this.el.addEventListener('click', (e) => {
        const b = e.target.closest('[data-line-qty]');
        if (b) this.change(b.dataset.key, Number(b.dataset.lineQty));
      });
    },
    open() {
      if (!this.el || this.el.classList.contains('is-open')) return;
      this.opener = document.activeElement;
      this.el.classList.add('is-open'); this.el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('has-cart');
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.background = $$('.main, .header-wrapper, .announce, .footer, [data-help], [data-satc]').map((el) => ({ el, inert: el.inert }));
      this.background.forEach(({ el }) => { el.inert = true; });
      $('.drawer__panel', this.el).focus();
    },
    close() {
      if (!this.el || !this.el.classList.contains('is-open')) return;
      this.el.classList.remove('is-open'); this.el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('has-cart');
      document.body.style.overflow = this.previousOverflow || '';
      this.background.forEach(({ el, inert }) => { el.inert = el.matches('[data-satc]') ? !el.classList.contains('is-visible') : inert; });
      if (this.opener && this.opener.isConnected && !this.opener.disabled && !this.opener.closest('[inert]')) this.opener.focus();
    },
    async refresh(open) {
      const response = await fetch(D.routes.cart + '.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(D.strings.cartRetry || D.strings.error);
      const cart = await response.json();
      this.render(cart);
      if (open && this.el) this.open();
      return cart;
    },
    async change(key, quantity) {
      if (this.busy) return;
      this.busy = true;
      const focused = document.activeElement;
      const controls = $$('[data-line-qty], button[name="checkout"]', this.el);
      controls.forEach((b) => { b.disabled = true; });
      this.el.setAttribute('aria-busy', 'true');
      const status = $('[data-cart-status]', this.el);
      if (status) { status.hidden = false; status.textContent = D.strings.cartUpdating; }
      try {
        const res = await fetch(D.routes.cartChange + '.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || D.strings.error);
        this.render(data);
        const replacement = $$('[data-line-qty]', this.el).find((b) => b.dataset.key === key && b.getAttribute('aria-label') === focused.getAttribute('aria-label'));
        (replacement || $('button[data-cart-close]', this.el)).focus();
        if (status) { status.textContent = ''; status.hidden = true; }
      } catch (err) {
        if (status) { status.hidden = false; status.textContent = err.message || D.strings.error; }
      } finally {
        this.busy = false; this.el.setAttribute('aria-busy', 'false');
        $$('[data-line-qty], button[name="checkout"]', this.el).forEach((b) => { b.disabled = b.name === 'checkout' && !this.itemCount; });
      }
    },
    render(cart) {
      this.itemCount = cart.item_count;
      $$('[data-cart-count]').forEach((n) => { n.textContent = cart.item_count; n.dataset.count = cart.item_count; });
      if (!this.el) return;
      $('[data-cart-total]', this.el).textContent = formatMoney(cart.total_price);
      const bar = $('[data-ship-bar]', this.el);
      if (bar) {
        const th = Number(bar.dataset.threshold) || 0;
        const left = Math.max(0, th - cart.total_price);
        $('[data-ship-text]', bar).textContent = left > 0
          ? `You're ${formatMoney(left)} away from free delivery`
          : 'Free UK delivery unlocked';
        $('[data-ship-fill]', bar).style.width = `${clamp(cart.total_price / th, 0, 1) * 100}%`;
      }
      const body = $('[data-cart-items]', this.el);
      $('.drawer__foot', this.el).hidden = !cart.item_count;
      $('button[name="checkout"]', this.el).disabled = !cart.item_count;
      if (!cart.item_count) {
        body.innerHTML = `<div class="drawer__empty"><p>${D.strings.empty}</p><a class="btn" href="${D.routes.root}collections/all">Shop now</a></div>`;
        return;
      }
      const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const cartPhoto = (item) => {
        const handle = item.handle || (item.url || '').split('/products/')[1]?.split('?')[0];
        const photos = D.productPhotos || {};
        return photos[handle + ':' + item.variant_title] || photos[handle] || item.image;
      };
      body.innerHTML = cart.items.map((i) => `
        <div class="line">
          <a class="line__img" href="${esc(i.url)}">${cartPhoto(i) ? `<img src="${esc(cartPhoto(i))}" alt="${esc(i.product_title)}" width="100" height="100" loading="lazy">` : ''}</a>
          <div>
            <a class="line__title" href="${esc(i.url)}">${esc(i.product_title)}</a>
            ${i.variant_title ? `<span class="line__variant">${esc(i.variant_title)}</span>` : ''}
            <div class="qty" role="group" aria-label="Quantity">
              <button type="button" data-line-qty="${i.quantity - 1}" data-key="${esc(i.key)}" aria-label="Decrease">−</button>
              <input value="${i.quantity}" readonly aria-label="Quantity">
              <button type="button" data-line-qty="${i.quantity + 1}" data-key="${esc(i.key)}" aria-label="Increase">+</button>
            </div>
            <div><button class="line__remove" type="button" data-line-qty="0" data-key="${esc(i.key)}">${D.strings.remove}</button></div>
          </div>
          <span class="line__price">${formatMoney(i.final_line_price)}</span>
        </div>`).join('');
    }
  };

  /* ------------------------------------------------------------------
     Boot
  ------------------------------------------------------------------ */
  const registry = { hero: initHero, finale: initFinale, scrub: initScrubStatic };
  const heroIntros = [];

  function initSection(el) {
    const fn = registry[el.dataset.sectionType];
    if (fn) {
      const r = fn(el);
      if (typeof r === 'function') heroIntros.push(r);
    }
    initAutoplay(el);
    initFaq(el);
    initProduct(el);
  }

  async function boot() {
    initHeader();
    Cart.init();
    const intro = runIntro();
    $$('[data-section-type]').forEach(initSection);
    initStickyBar();
    await intro;
    heroIntros.forEach((fn) => fn());
    if (motion) ScrollTrigger.refresh();
    if (Cart.el) Cart.refresh(false).catch(() => {});
  }

  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector('[data-section-type]');
    if (el) { const count = heroIntros.length; initSection(el); if (heroIntros.length > count) heroIntros.pop()(); initStickyBar(); if (motion) ScrollTrigger.refresh(); }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
